import React, { useEffect, useMemo } from 'react'
import { match, P } from 'ts-pattern'
import classnames from 'classnames/bind'
// @ts-ignore — @adc/ui-adc ships partial types
import {
  Text,
  useApplicationModals,
  ModalIds,
  hooks as adcHooks,
} from '@adc/ui-adc'
import type { CampaignToken } from '../../util/campaign'
import auctionLogo from '../../assets/auction-logo.svg'
import styles from './styles.scss'

const cx = classnames.bind(styles)

/**
 * Campaign welcome modal.
 *
 * Rendered on a curated SRP landing when a valid campaign token is present
 * (see util/campaign). Opens a centered modal overlay matching the Marketing
 * Mailer design (Figma node 12846-1102) — ADC logomark, title, body copy, and
 * a "Browse Properties" CTA.
 *
 * Uses the design-system application-modals manager (`useApplicationModals`)
 * so exactly one modal is active at a time — the same registry SaveSearch and
 * authentication modals go through. It opens the built-in `general`
 * (GeneralModal) renderer, which supplies the DS Modal shell: dimmer, X close
 * icon, Content, and Actions footer.
 *
 * Each treatment is a data object (title, body, ctaElmId). ctaElmId gives each
 * variant's CTA a unique data-elm-id even though the label is identical.
 *
 * Selection matches on the whole [campaign, variant] tuple (1-based):
 *
 *   .with([5, 1], () => bespokeCampaign5Treatment)  // exact campaign + variant
 *   .with([P.number, 1], () => welcomeBack)         // any campaign, variant 1
 */

interface TreatmentData {
  title: string
  body: string
  /** Unique data-elm-id for the CTA button (label is shared across variants). */
  ctaElmId: string
}

const welcomeBack: TreatmentData = {
  title: 'Welcome Back',
  body: 'More foreclosure properties are available in your area. Explore the latest deals and see what\u2019s changed since your last visit.',
  ctaElmId: 'welcome_back_cta',
}

const welcomeNew: TreatmentData = {
  title: 'Welcome to Auction.com',
  body: 'Expand your search and discover nearby foreclosure properties not listed on the MLS.',
  ctaElmId: 'welcome_new_cta',
}

const treatmentFor = ({
  campaign,
  variant,
}: CampaignToken): TreatmentData | null =>
  match<[number, number], TreatmentData | null>([campaign, variant])
    .with([P.number, 1], () => welcomeBack)
    .with([P.number, 2], () => welcomeNew)
    .otherwise(() => null)

export interface CampaignWelcomeModalProps {
  /** Decoded campaign token (1-based campaign + variant). */
  campaign: CampaignToken
  elmId?: string
}

const CampaignWelcomeModal: React.FC<CampaignWelcomeModalProps> = ({
  campaign,
  elmId = 'campaign_welcome',
}) => {
  const treatment = treatmentFor(campaign)
  const breakpoint = adcHooks.useBreakpoints()
  const isMobile = breakpoint === 'mobile'

  // Build the GeneralModal props: empty title (X close only), centered logo +
  // heading + body as content, and the CTA as a single primary action button.
  const modalProps = useMemo(() => {
    if (!treatment) return null
    return {
      title: '',
      // Mobile slide-up panel sizes by SIZE_WIDTH_MAP — 'fullscreen' = 100%
      // width. Desktop keeps the compact centered 'mini' modal.
      size: isMobile ? 'fullscreen' : 'mini',
      elmId,
      // Bottom-sheet presentation on mobile; centered modal on desktop.
      // fullScreen defaults to 'mobileOnly' and takes precedence over slideUp,
      // so it must be 'never' for the slide-up panel to render.
      fullScreen: 'never',
      slideUp: 'mobileOnly',
      children: (
        <div data-elm-id={`${elmId}_content`} className={cx('content')}>
          <img
            src={auctionLogo}
            alt='Auction.com'
            width={42}
            height={36}
            className={cx('logo')}
          />
          <div className='u-mt-2'>
            <Text.Header level={2}>{treatment.title}</Text.Header>
          </div>
          <div className='u-mt-2'>
            <Text.Body size='normal'>{treatment.body}</Text.Body>
          </div>
        </div>
      ),
      actions: [
        {
          label: 'Browse Properties',
          role: 'primary',
          isStretch: true,
          // Cancel semantic-ui's .actions > .button left margin, which pushes
          // the stretched button off-center on desktop.
          className: cx('cta'),
          elmId: treatment.ctaElmId,
        },
      ],
    }
  }, [treatment, elmId, isMobile])

  // Owner-scoped modal handle — props stay synced while open and only this
  // caller can drive the general modal.
  const { open } = useApplicationModals(ModalIds.General, modalProps)

  useEffect(() => {
    if (treatment) open()
  }, [treatment, open])

  return null
}

export default CampaignWelcomeModal
